/* bb-subscription-addtocart.js
 * Blind Barrels — shared subscription add-to-cart helper.
 *
 * Purpose:
 *   Replace the legacy "add then window.location='/checkout'" flow used by
 *   subscription-box.liquid, subscription-plans-annual-hero.liquid, and
 *   per-template inline addToCartWithSellingPlan() scripts. All entry points
 *   should add to cart, refresh the cart drawer with rendered section HTML,
 *   then open the drawer.
 *
 * Exposes:
 *   window.bbAddSubscriptionToCart(items, opts)
 *     items: array of { id, quantity, selling_plan? }
 *     opts:  { onSuccess?, onError? }
 *
 * Side effects:
 *   On DOMContentLoaded, overrides window.addToCartWithSellingPlan so any
 *   inline <script> definitions in template custom_liquid blocks that ran
 *   earlier are replaced with the drawer-friendly version.
 */
(function () {
  // Returns 'native' if Dawn's renderContents handled it (and will open the
  // drawer itself via its internal setTimeout), 'manual' if we swapped HTML
  // ourselves (caller must open the drawer), or false if no drawer on page.
  function refreshCartDrawerFromResponse(response) {
    if (!response || !response.sections) return false;
    var drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.remove('is-empty');

    if (drawer && typeof drawer.renderContents === 'function') {
      try {
        drawer.renderContents(response);
        drawer.classList.remove('is-empty');
        // Dawn's renderContents schedules this.open() via setTimeout.
        // Do NOT call open() again here or in the caller — double-open
        // restarts the slide animation and looks like a flicker.
        return 'native';
      } catch (e) {
        console.warn('[bb-subscription-addtocart] drawer.renderContents failed:', e);
      }
    }

    try {
      var parser = new DOMParser();
      var drawerHtml = response.sections['cart-drawer'];
      if (drawerHtml) {
        var drawerSection = document.getElementById('CartDrawer');
        if (drawerSection) {
          var fresh = parser.parseFromString(drawerHtml, 'text/html').querySelector('#CartDrawer');
          if (fresh) drawerSection.innerHTML = fresh.innerHTML;
        }
      }
      var bubbleHtml = response.sections['cart-icon-bubble'];
      if (bubbleHtml) {
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          var freshBubble = parser.parseFromString(bubbleHtml, 'text/html').querySelector('#cart-icon-bubble');
          if (freshBubble) bubble.innerHTML = freshBubble.innerHTML;
        }
      }
      return 'manual';
    } catch (e) {
      console.warn('[bb-subscription-addtocart] manual section refresh failed:', e);
      return false;
    }
  }

  function tryOpenCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    var triggerSelectors = [
      '#cart-icon-bubble',
      'button[aria-controls*="CartDrawer"]',
      '[data-drawer-id*="cart"]',
      '.js-toggle-cart',
      '.header__icon--cart, .site-header__cart',
      'button[name="open-cart"], a[href="#cart-drawer"]',
      'a[href="/cart"]'
    ];
    for (var i = 0; i < triggerSelectors.length; i++) {
      var el = document.querySelector(triggerSelectors[i]);
      if (el) {
        el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        return true;
      }
    }
    return false;
  }

  function bbAddSubscriptionToCart(items, opts) {
    opts = opts || {};
    if (!items || !items.length) return Promise.resolve(null);

    var body = {
      items: items,
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: window.location.pathname
    };

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    })
      .then(function (res) {
        if (!res.ok) {
          return res.text().then(function (text) {
            throw new Error(text || 'Add to cart failed: ' + res.status);
          });
        }
        return res.json();
      })
      .then(function (response) {
        var mode = refreshCartDrawerFromResponse(response);
        // Only open the drawer ourselves if Dawn's renderContents wasn't used.
        // When mode === 'native', cart-drawer.js already schedules open() in a
        // setTimeout — calling it again here causes a visible re-animation
        // ("flicker": drawer appears, disappears, reappears).
        if (mode !== 'native') {
          tryOpenCartDrawer();
        }
        if (typeof opts.onSuccess === 'function') opts.onSuccess(response);
        return response;
      })
      .catch(function (err) {
        console.error('[bb-subscription-addtocart]', err);
        if (typeof opts.onError === 'function') opts.onError(err);
        throw err;
      });
  }

  window.bbAddSubscriptionToCart = bbAddSubscriptionToCart;

  // Override the legacy global defined inline in template custom_liquid blocks.
  // Those inline <script>s execute during parse and assign
  // window.addToCartWithSellingPlan = function(){ fetch + redirect }.
  // By DOMContentLoaded all such inline scripts have run, so reassigning here
  // wins and the new drawer flow takes effect for every page that uses it.
  function installLegacyOverride() {
    window.addToCartWithSellingPlan = function (variantId, sellingPlanId) {
      var id = parseInt(variantId, 10);
      if (!id) {
        console.error('[bb-subscription-addtocart] invalid variantId:', variantId);
        return;
      }
      var item = { id: id, quantity: 1 };
      var plan = parseInt(sellingPlanId, 10);
      if (plan) item.selling_plan = plan;
      bbAddSubscriptionToCart([item]);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installLegacyOverride);
  } else {
    installLegacyOverride();
  }
})();
